import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import objectSupport from 'dayjs/plugin/objectSupport';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';

import { t } from '../services/translation/translation';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(objectSupport);
dayjs.extend(weekday);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeeksInYear);
dayjs.extend(isLeapYear);
dayjs.extend(isoWeek);

export const isParisTz = () => {
  const now = dayjs();
  return now.diff(now.tz('Europe/Paris', true), 'hours') === 0;
};

export const parseDate = (date: string, parseFormatString: string) => dayjs(date, parseFormatString).valueOf();

export const formatDate = (date: number, formatString: string) => dayjs(date).format(formatString);

export const formatDateForParisTZ = (date: number, formatString: string, withSuffix = true) => {
  const formattedDate = dayjs(date).tz('Europe/Paris').format(formatString);
  return `${formattedDate}${withSuffix && !isParisTz() ? t('common.local-time') : ''}`;
};

export const dateDiff = (from: Date | number, to: Date | number, unit: dayjs.OpUnitType) => dayjs(from).diff(to, unit);

export const formatTimeBySeconds = (value: number) => {
  const h = Math.floor(value / 60)
    .toString()
    .padStart(2, '0');
  const m = Math.floor(value % 60)
    .toString()
    .padStart(2, '0');
  return `${h}:${m}`;
};

export const dateAdd = (date: Date | number, amount: number, unit: dayjs.ManipulateType) =>
  dayjs(date).add(amount, unit);

export const formatRelativeDate = (from: number, to: number) => {
  const fromDate = dayjs(from);
  const toDate = dayjs(to);
  const diff = fromDate.diff(toDate, 'minutes');

  return diff < 0 ? t('common.now') : dayjs.duration({ minutes: diff }).humanize(true);
};

export const isPast = (date: Date | number) => dayjs(date).isBefore(Date.now());

export const getParisTzOffset = () => {
  const now = dayjs();
  return now.tz('UTC', true).diff(now.tz('Europe/Paris', true), 'minutes');
};

export const addMinutes = (date: number, minutes: number) => dayjs(date).add(minutes, 'minutes').valueOf();
export const addHours = (date: number, hours: number) => dayjs(date).add(hours, 'hours').valueOf();
export const addDays = (date: number, days: number) => dayjs(date).add(days, 'days').valueOf();
export const addMonths = (date: number, months: number) => dayjs(date).add(months, 'months').valueOf();
export const subtractDays = (date: number, days: number) => dayjs(date).subtract(days, 'days').valueOf();
export const subtractMonths = (date: number, months: number) => dayjs(date).subtract(months, 'months').valueOf();
export const isBefore = (date1: number, date2: number) => dayjs(date1).isBefore(date2);
export const isAfter = (date1: number, date2: number) => dayjs(date1).isAfter(date2);

export const humanizeDuration = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.ceil((seconds % 3600) / 60);

  if (hours > 0 && minutes > 0) return t('common.xHours-xMin', { hours, minutes });

  if (hours > 0) return t('common.xHour', { hours });

  return t('common.xMin', { minutes });
};

export const startOfDay = (date: number) => dayjs(date).startOf('day').valueOf();
export const startOfMonth = (date: number) => dayjs(date).startOf('month').valueOf();
export const endOfDay = (date: number) => dayjs(date).endOf('day').valueOf();
export const endOfMonth = (date: number) => dayjs(date).endOf('month').valueOf();

export const getDaysInMonth = (year: number, month: number) => dayjs(`${year}-${month + 1}-15`).daysInMonth();
export const getWeeksInYear = (year: number) => dayjs(`${year}-06-01`).isoWeeksInYear();
export const getDate = (date: number) => dayjs(date).date();
export const getDayOfWeek = (date: number) => dayjs(date).weekday();
export const getWeek = (date: number) => dayjs(date).isoWeek();
export const getYear = (date: number) => dayjs(date).year();
export type MonthValue = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;
export const getMonth = (date: number): MonthValue => dayjs(date).month() as MonthValue;
